import { paginationItemClasses } from '@mui/material/PaginationItem';
import type {
  Components,
  ComponentsVariants,
  Theme,
} from '@mui/material/styles';

import { varAlpha } from '../../styles';

// ----------------------------------------------------------------------

// NEW VARIANT
declare module '@mui/material/Pagination' {
  interface PaginationPropsVariantOverrides {
    soft: true;
  }

  interface PaginationPropsColorOverrides {
    info: true;
    success: true;
    warning: true;
    error: true;
  }
}

const COLORS = [
  'primary',
  'secondary',
  'info',
  'success',
  'warning',
  'error',
] as const;

// ----------------------------------------------------------------------

const softVariant: Record<string, ComponentsVariants<Theme>['MuiPagination']> =
  {
    colors: COLORS.map((color) => ({
      props: ({ ownerState }) =>
        !ownerState.disabled &&
        ownerState.variant === 'soft' &&
        ownerState.color === color,
      style: ({ theme }) => ({
        [`& .${paginationItemClasses.root}`]: {
          [`&.${paginationItemClasses.selected}`]: {
            fontWeight: theme.typography.fontWeightSemiBold,
            color:
              theme.palette.mode === 'dark'
                ? theme.palette[color].light
                : theme.palette[color].dark,
            backgroundColor: varAlpha(theme.palette[color].mainChannel, 0.08),
            '&:hover': {
              backgroundColor: varAlpha(theme.palette[color].mainChannel, 0.16),
            },
          },
        },
      }),
    })),
    standardColor: [
      {
        props: ({ ownerState }) =>
          ownerState.variant === 'soft' && ownerState.color === 'standard',
        style: ({ theme }) => ({
          [`& .${paginationItemClasses.root}`]: {
            [`&.${paginationItemClasses.selected}`]: {
              fontWeight: theme.typography.fontWeightSemiBold,
              backgroundColor: varAlpha(theme.palette.grey['500Channel'], 0.08),
              '&:hover': {
                backgroundColor: varAlpha(
                  theme.palette.grey['500Channel'],
                  0.16
                ),
              },
            },
          },
        }),
      },
    ],
  };

// ----------------------------------------------------------------------

const MuiPagination: Components<Theme>['MuiPagination'] = {
  /** **************************************
   * VARIANTS
   *************************************** */
  variants: [
    /**
     * @variant soft
     */
    ...[...softVariant.standardColor!, ...softVariant.colors!],
  ],

  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    /**
     * @variant text
     */
    text: ({ ownerState, theme }) => ({
      [`& .${paginationItemClasses.root}`]: {
        [`&.${paginationItemClasses.selected}`]: {
          fontWeight: theme.typography.fontWeightSemiBold,
          ...(ownerState.color === 'standard' && {
            color:
              theme.palette.mode === 'dark'
                ? theme.palette.grey[800]
                : theme.palette.common.white,
            backgroundColor: theme.palette.text.primary,
            '&:hover': {
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? theme.palette.grey[100]
                  : theme.palette.grey[700],
            },
          }),
        },
      },
    }),
    /**
     * @variant outlined
     */
    outlined: ({ ownerState, theme }) => ({
      [`& .${paginationItemClasses.root}`]: {
        borderColor: varAlpha(theme.palette.grey['500Channel'], 0.24),
        [`&.${paginationItemClasses.selected}`]: {
          borderColor: 'currentColor',
          fontWeight: theme.typography.fontWeightSemiBold,
          ...(ownerState.color === 'standard' && {
            backgroundColor: varAlpha(theme.palette.grey['500Channel'], 0.08),
          }),
        },
      },
    }),
  },
};

// ----------------------------------------------------------------------

export const pagination = { MuiPagination };
